import React, { FC } from 'react';

import { useTranslation } from '@modernloop/i18n';

import { StringOrgPref } from 'src/generated/mloop-graphql';

import { useUrlSearchParams, SearchParamKnownKeys } from 'src/hooks/useUrlSearchParams';

import { SettingsCard, SettingsCardSection, SettingsFixedWidthPage, SettingsStack } from '../shared/layout';

import CandidateIdentitySettings from './CandidateIdentitySettings';
import TokenizedTitleTemplateSetting from '../shared/TokenizedTitleTemplateSetting';
import {
  CODING_LINK_PREVIEW_SAMPLE_VALUES,
  CODING_LINK_TITLE_TOKENS,
  GENERIC_TITLE,
} from '../shared/tokenizedTitleTemplate';

type Props = {
  disabled: boolean;
};

/** Coding tools that get their own per-tool settings page. Matches the `tool` search param value. */
export enum CodingToolParam {
  CODERPAD = 'CODERPAD',
  CODILITY = 'CODILITY',
  CODESIGNAL = 'CODESIGNAL',
  HACKERRANK = 'HACKERRANK',
}

type CodingToolConfig = {
  displayName: string;
  /** Present only for tools that send a link title (CoderPad, Codility, HackerRank). */
  titlePref?: StringOrgPref;
  /** Present only for tools that send the candidate name (CodeSignal, HackerRank). */
  namePref?: StringOrgPref;
  /** Present only for tools that send the candidate email (Codility, CodeSignal, HackerRank). */
  emailPref?: StringOrgPref;
};

// Single source of truth for the capability matrix (see docs/plans/per-tool-coding-link-settings.md):
// a tool only exposes a control for a field it can actually send to the vendor.
const CODING_TOOL_CONFIG: Record<CodingToolParam, CodingToolConfig> = {
  [CodingToolParam.CODERPAD]: {
    displayName: 'CoderPad',
    titlePref: StringOrgPref.StrCoderpadCodingLinkTitleTemplate,
  },
  [CodingToolParam.CODILITY]: {
    displayName: 'Codility',
    titlePref: StringOrgPref.StrCodilityCodingLinkTitleTemplate,
    emailPref: StringOrgPref.StrCodilityCandidateEmail,
  },
  [CodingToolParam.CODESIGNAL]: {
    displayName: 'CodeSignal',
    namePref: StringOrgPref.StrCodesignalCandidateName,
    emailPref: StringOrgPref.StrCodesignalCandidateEmail,
  },
  [CodingToolParam.HACKERRANK]: {
    displayName: 'HackerRank',
    titlePref: StringOrgPref.StrHackerrankCodingLinkTitleTemplate,
    namePref: StringOrgPref.StrHackerrankCandidateName,
    emailPref: StringOrgPref.StrHackerrankCandidateEmail,
  },
};

const isCodingToolParam = (value: string | null): value is CodingToolParam =>
  value !== null && Object.prototype.hasOwnProperty.call(CODING_TOOL_CONFIG, value);

/**
 * Per-tool coding-tool settings page. The tool is chosen via the `tool` search param
 * (e.g. `?tool=HACKERRANK`); the page renders only the cards that tool can use per the capability
 * matrix: a link-title card (when the tool sends a title) and a candidate-privacy card (when the
 * tool sends the candidate name and/or email).
 */
const CodingTool: FC<Props> = ({ disabled }) => {
  const { t } = useTranslation();
  const query = useUrlSearchParams();
  const toolParam = query.get(SearchParamKnownKeys.codingTool);

  if (!isCodingToolParam(toolParam)) {
    return null;
  }

  const { displayName, titlePref, namePref, emailPref } = CODING_TOOL_CONFIG[toolParam];
  const hasIdentity = Boolean(namePref || emailPref);

  return (
    <SettingsFixedWidthPage
      title={t('hiring-dashboard-settings-integrations:settings.codingTool.pageTitle', { tool: displayName })}
      description={t('hiring-dashboard-settings-integrations:settings.codingTool.pageDescription', {
        tool: displayName,
      })}
    >
      <SettingsStack>
        {titlePref && (
          <SettingsCard
            title={t('hiring-dashboard-settings-integrations:settings.collaborationLinks.titleTemplate.cardTitle')}
            disabled={disabled}
          >
            <SettingsCardSection>
              <TokenizedTitleTemplateSetting
                titlePref={titlePref}
                tokens={CODING_LINK_TITLE_TOKENS}
                previewSampleValues={CODING_LINK_PREVIEW_SAMPLE_VALUES}
                tokenLabels={{
                  CANDIDATE_NAME: t(
                    'hiring-dashboard-settings-integrations:settings.collaborationLinks.titleTemplate.token.candidateName'
                  ),
                  CANDIDATE_EMAIL_ADDRESS: t(
                    'hiring-dashboard-settings-integrations:settings.collaborationLinks.titleTemplate.token.candidateEmailAddress'
                  ),
                  JOB_NAME: t(
                    'hiring-dashboard-settings-integrations:settings.collaborationLinks.titleTemplate.token.jobName'
                  ),
                  JOB_STAGE_NAME: t(
                    'hiring-dashboard-settings-integrations:settings.collaborationLinks.titleTemplate.token.jobStageName'
                  ),
                  ATS_APPLICATION_ID: t(
                    'hiring-dashboard-settings-integrations:settings.collaborationLinks.titleTemplate.token.atsApplicationId'
                  ),
                }}
                description={t(
                  'hiring-dashboard-settings-integrations:settings.collaborationLinks.titleTemplate.description'
                )}
                placeholder={t(
                  'hiring-dashboard-settings-integrations:settings.collaborationLinks.titleTemplate.placeholder'
                )}
                emptyStateInfo={t(
                  'hiring-dashboard-settings-integrations:settings.collaborationLinks.titleTemplate.emptyStateInfo'
                )}
                emptyStateInfoOmittedEmail={t(
                  'hiring-dashboard-settings-integrations:settings.collaborationLinks.titleTemplate.emptyStateInfoOmittedEmail'
                )}
                omittedEmailWarning={t(
                  'hiring-dashboard-settings-integrations:settings.collaborationLinks.titleTemplate.omittedEmailWarning'
                )}
                omittedNameWarning={t(
                  'hiring-dashboard-settings-integrations:settings.collaborationLinks.titleTemplate.omittedNameWarning'
                )}
                omittedNameAndEmailWarning={t(
                  'hiring-dashboard-settings-integrations:settings.collaborationLinks.titleTemplate.omittedNameAndEmailWarning'
                )}
                renderPreviewLabel={(preview) =>
                  t('hiring-dashboard-settings-integrations:settings.collaborationLinks.titleTemplate.preview', {
                    preview,
                  })
                }
                previewFallback={GENERIC_TITLE}
                saveLabel={t('hiring-dashboard-settings-integrations:settings.collaborationLinks.titleTemplate.save')}
                invalidMessage={t(
                  'hiring-dashboard-settings-integrations:settings.collaborationLinks.titleTemplate.invalid'
                )}
                namePref={namePref}
                emailPref={emailPref}
                disabled={disabled}
              />
            </SettingsCardSection>
          </SettingsCard>
        )}
        {hasIdentity && (
          <SettingsCard
            title={t('hiring-dashboard-settings-integrations:settings.collaborationLinks.identity.cardTitle')}
            disabled={disabled}
          >
            <SettingsCardSection>
              <CandidateIdentitySettings namePref={namePref} emailPref={emailPref} disabled={disabled} />
            </SettingsCardSection>
          </SettingsCard>
        )}
      </SettingsStack>
    </SettingsFixedWidthPage>
  );
};

export default CodingTool;
